/*
 * Copyright (c) Nexign, JSC, 2019-2020.
 */
/**
 * Storage sagas
 *
 * @author Mikhail Kokoulin
 * @author Aleksandr Perkhunov
 * @author Maxim Fedorov
 * @author Evgenij Tyan
 * @author Anton Panasenko
 * @version 1.3.0
 * @since 1.0.0
 */

import { LOCATION_CHANGE } from 'connected-react-router';
import { SagaIterator } from 'redux-saga';
import { all, fork, put, select, takeEvery } from 'redux-saga/effects';

import watchTimerSaga from '../../components/Table/sagas/footerTickSagas';
import { CHECK_GUARD_SUCCESSFUL } from '../role/constants/actionConstants';
import { makeSelectIsDensityListViewEnabled } from '../role/selectors/guardSelector';
import { finishLoadStorageData, removeStorageDataSuccess, setStorageDataSuccess } from './storageActions';
import {
    COOKIES,
    COOKIES_FOR_REMOVE, COOKIES_PARSE,
    LOAD_STORAGE_DATA_START,
    LOCAL_STORAGE,
    LOCAL_STORAGE_FOR_REMOVE,
    REMOVE_COOKIE_DATA,
    REMOVE_STORAGE_DATA_START,
    SESSION_STORAGE,
    SET_COOKIE_DATA,
    SET_LOCAL_STORAGE_DATA,
    SET_SESSION_STORAGE_DATA,
    SET_STORAGE_DATA_SUCCESS,
} from './storageConstants';
import { ILocalStorage, initialStorageState, ISessionStorage } from './storageReducer';
import {
    ECookieKey,
    TRemoveCookieAction,
    TSetCookieAction,
    TSetLocalStorageDataAction,
    TSetSessionStorageDataAction,
} from './storageTypes';
import {
    getCookieByName,
    getCookieByNameAsJson,
    getItemFromLocalStorage,
    getItemFromSessionStorage,
    removeCookie,
    removeItemLocalStorage,
    setCookie,
    setItemFromSessionStorage,
    setItemToLocalStorage,
} from './storageUtils';

/**
 * Saga to initialize storage state
 * @returns {SagaIterator}
 */
export function* loadStorageDataSaga(): SagaIterator {
    try {
        const cookies = { ...initialStorageState.cookies };
        const localStorage: ILocalStorage = { ...initialStorageState.localStorage };
        const sessionStorage: ISessionStorage = { ...initialStorageState.sessionStorage };

        COOKIES.forEach((key: ECookieKey) => {
            // @ts-ignore
            cookies[key] = getCookieByName(key);
        });
        const isDensityListViewEnabled = !!(yield select(makeSelectIsDensityListViewEnabled()));
        cookies[ECookieKey.DENSITY_LIST_VIEW] = isDensityListViewEnabled &&
            getCookieByNameAsJson(ECookieKey.DENSITY_LIST_VIEW, isDensityListViewEnabled);
        LOCAL_STORAGE.forEach((key: string) => {
            localStorage[key] = getItemFromLocalStorage(key);
        });
        SESSION_STORAGE.forEach((key: string) => {
            sessionStorage[key] = getItemFromSessionStorage(key);
        });
        yield put(finishLoadStorageData({ cookies, localStorage, sessionStorage }));
    } catch (error) {
        console.log(error);
    }
}

/**
 * Saga for removal storage state
 * @returns {SagaIterator}
 */
export function* removeStorageDataSaga(): SagaIterator {
    try {
        COOKIES_FOR_REMOVE.forEach((cookieName: string) => {
            removeCookie(cookieName);
        });
        LOCAL_STORAGE_FOR_REMOVE.forEach((key: string) => {
            removeItemLocalStorage(key);
        });
        yield put(removeStorageDataSuccess());
    } catch (error) {
        console.log(error);
    }
}

/**
 * Saga for set data in local storage
 * @param action
 */
export function* setLocalStorageDataSaga(action: TSetLocalStorageDataAction): SagaIterator {
    const { key, value } = action.payload;
    try {
        setItemToLocalStorage(key, JSON.stringify(value));
    } catch (error) {
        console.log(error);
    }
}

export function* setSessionStorageDataSaga(action: TSetSessionStorageDataAction): SagaIterator {
    const { key, value } = action.payload;
    try {
        setItemFromSessionStorage(key, value);
        yield put(setStorageDataSuccess());
    } catch (error) {
        console.log(error);
    }
}

export function* setCookieSaga(action: TSetCookieAction): SagaIterator {
    const { key, value, options = {} } = action.payload;
    try {
        if (COOKIES_PARSE.indexOf(key)>=0) {
            setCookie(key, JSON.stringify(value), options);
        } else {
            setCookie(key, '' + value, options);
        }
    } catch (error) {
        console.log(error);
    }
}

export function* storageRemoveCookieSaga(action: TRemoveCookieAction): SagaIterator {
    const { key } = action.payload;
    try {
        removeCookie(key);
    } catch (error) {
        console.log(error);
    }
}

export function* storageSaga() {
    yield all([
        takeEvery([LOAD_STORAGE_DATA_START, SET_STORAGE_DATA_SUCCESS], loadStorageDataSaga),
        takeEvery(REMOVE_STORAGE_DATA_START, removeStorageDataSaga),
        takeEvery(REMOVE_COOKIE_DATA, storageRemoveCookieSaga),
        takeEvery(SET_COOKIE_DATA, setCookieSaga),
        takeEvery([LOCATION_CHANGE, CHECK_GUARD_SUCCESSFUL], loadStorageDataSaga),
        takeEvery(SET_LOCAL_STORAGE_DATA, setLocalStorageDataSaga),
        takeEvery(SET_SESSION_STORAGE_DATA, setSessionStorageDataSaga),
        fork(watchTimerSaga),
    ]);
}
